import { get } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import type { Hotelinfo } from 'frontend-utils/models';
import type { RateStrategy, Rsbaserate, Rssubtype, Rstype } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  NO_DATA_SUBSORT,
  RSTYPES_IN_CANCELLATION_POLICY_SECTION,
  TYPE_ID_BEST_FLEX,
  TYPE_ID_SEMI_FLEX,
} from 'lumen/utils/constants/rate-strategy-constants';
import { formatCancellationpolicyValue } from 'lumen/utils/strategy/format-helper-utils';

// Rate strategy cancellation policy
export default class Rscancellationpolicy extends Model {
  [Type] = 'rscancellationpolicy' as const;

  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;

  @belongsTo('rscancellationpolicy', {
    async: false,
    inverse: 'historicEntities',
  })
  declare latestEntity: Rscancellationpolicy;

  @belongsTo('rate-strategy', {
    async: false,
    inverse: 'rscancellationpolicies',
  })
  declare rateStrategy: RateStrategy;

  // Only for Best Flex (Semi Flex and Non-refundable baserates are linked to their strategy)
  @belongsTo('rsbaserate', { async: true, inverse: null })
  declare rsbaserate: Rsbaserate;

  // OpenAPI spec: https://git.mylighthouse.com/OTA-Insight/pricing-insight/-/blob/master/api/openapi-spec/openapi.yaml
  @belongsTo('rssubtype', { async: true, inverse: 'rscancellationpolicies' })
  declare rssubtype: Rssubtype;

  @hasMany('rscancellationpolicy', { async: false, inverse: 'latestEntity' })
  declare historicEntities: HasMany<Rscancellationpolicy>;

  @attr('number') declare days: number;
  @attr('string') declare message: string;
  @attr('string') declare time: string;

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare rsSelections: Services['rs-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baserateWithFallback() {
    // Not offered edge case
    // In this case we still need to show a fallback price, with the appropriate icons. Do a fallback to the base baserates in this case
    // RS Spec link: https://docs.google.com/document/d/1jhgazIAKAPpRChuw2bNoVIFjF7_fKDoQPkJTCEVPhHk/edit?pli=1#heading=h.enz1mu8jjfho
    if (
      this.isNotOffered &&
      /* eslint-disable @typescript-eslint/no-unsafe-member-access */
      !this.rsSelections.baseBaseRatesPerHotelInfo.get(this.hotelinfo.id)
        .hasNoBaseRate
      /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.rsSelections.baseBaseRatesPerHotelInfo.get(this.hotelinfo.id);
    }

    return this.rsbaserate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get changeOverTime() {
    if (this.hasNoDataForLatestOrHistoricReference) {
      return null;
    }

    // @ts-expect-error: Object is possibly 'undefined'.
    return this.days - this.historicEntity.days;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extractDateWeekOffset() {
    return this.rateStrategy?.extractDateWeekOffset;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChangeOverTime() {
    if (this.hasNoDataForLatestOrHistoricReference) {
      return this.intl.t('general.missing');
    }

    if (this.changeOverTime === 0 || this.changeOverTime === null) {
      return ''; // Show empty cell for no change
    }

    return formatCancellationpolicyValue(this.intl, this.changeOverTime);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedHistoricValueWithDetails() {
    if (!this.historicEntity) {
      return this.intl.t('general.missing').toString();
    }

    return (
      this.historicEntity.noDataMessage ??
      formatCancellationpolicyValue(
        this.intl,
        this.historicEntity.days,
        this.historicEntity.time,
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRate() {
    if (this.isSoldOut) {
      return this.intl.t('rates.soldout').toString();
    }

    if (this.isMissing || this.isNotOffered) {
      return this.intl.t('general.missing').toString();
    }

    // TODO: Check if `get()` is still necessary.
    // eslint-disable-next-line ember/no-get
    return get(this.rsbaserate, 'formattedBaseRate');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    return (
      this.noDataMessage ?? formatCancellationpolicyValue(this.intl, this.days)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValueWithDetails() {
    return (
      this.noDataMessage ??
      formatCancellationpolicyValue(this.intl, this.days, this.time)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoData() {
    return (
      this.isMissing ||
      this.isSoldOut ||
      this.isNonComputable ||
      this.isNotOffered
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoDataForLatestOrHistoricReference() {
    return (
      this.hasNoData || !this.historicEntity || this.historicEntity.hasNoData
    );
  }

  get historicEntity(): Rscancellationpolicy | undefined {
    // Use the reference period of the Strategy or Cancellation Policy section depending on the current rstype
    const referencePeriod = RSTYPES_IN_CANCELLATION_POLICY_SECTION.includes(
      this.rstype.name,
    )
      ? this.rsSelections.referencePeriodCancellationPolicySection
      : this.rsSelections.referencePeriodStrategySection;

    return this.historicEntities.find(
      (rsentity) => rsentity.extractDateWeekOffset === referencePeriod,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNonComputable() {
    // Reasons: https://docs.google.com/document/d/1jhgazIAKAPpRChuw2bNoVIFjF7_fKDoQPkJTCEVPhHk/edit#heading=h.xiq39iizrjuv
    // Member rates: hotel ONLY offering member rates
    return this.message === 'rates.noncomputable';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNotOffered() {
    return this.message === 'rates.notoffered';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOwnHotel() {
    return this.hotelinfo.id === this.hotelAndMarketService.hotel.hotelinfo.id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSoldOut() {
    return this.message === 'rates.soldout';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get noDataMessage() {
    if (this.isNotOffered) {
      // @ts-expect-error: Object is possibly 'null'.
      if (this.id.indexOf(TYPE_ID_SEMI_FLEX) !== -1) {
        return this.intl.t('strategy.no_semi_flex').toString();
      }

      // @ts-expect-error: Object is possibly 'null'.
      if (this.id.indexOf(TYPE_ID_BEST_FLEX) !== -1) {
        return this.intl.t('strategy.no_flex').toString();
      }
    }

    if (this.hasNoData) {
      return this.intl.t('general.missing').toString();
    }

    return null;
  }

  get rstype(): Rstype {
    // TODO: Check if `get()` is still necessary.
    // eslint-disable-next-line ember/no-get
    return get(this.rssubtype, 'rstype');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subsortOrder() {
    // When comparing two strategies with no data, subsort using the following order:
    // 1. Non-computable 2. Not offered 3. Soldout 4. Missing
    if (this.isNonComputable) {
      return NO_DATA_SUBSORT.NON_COMPUTABLE;
    }
    if (this.isNotOffered) {
      return NO_DATA_SUBSORT.NOT_OFFERED;
    }
    if (this.isSoldOut) {
      return NO_DATA_SUBSORT.SOLDOUT;
    }
    if (this.isMissing) {
      return NO_DATA_SUBSORT.MISSING;
    }

    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timeValueForSorting() {
    // Different cancellation policies can have the same days, but different time value
    // In case of same day values we can use this property to subsort further (the time strings aren't very handy for that)
    // examples of time values: 00:00, 23:59, 16:00,...
    // note: because it's a cutoff policy the expected DESC result would be: 23:59/16:00/00:00
    return dayjs.duration(this.time).asMilliseconds();
  }
}
